<template>
  <div class="banner">
    <div v-if="routeName === 'home'" class="home-banner">
      <a-carousel autoplay arrows>
        <template #prevArrow>
          <div class="custom-slick-arrow">
            <LeftOutlined />
          </div>
        </template>
        <template #nextArrow>
          <div class="custom-slick-arrow">
            <RightOutlined />
          </div>
        </template>
        <div
          v-for="(item, index) in content"
          :key="index"
          class="carousel-item"
        >
          <img
            :src="item.img"
            alt="图片"
            :loading="index === 0 ? 'eager' : 'lazy'"
            :fetchpriority="index === 0 ? 'high' : 'auto'"
            :decoding="index === 0 ? 'sync' : 'async'"
            :width="index === 0 ? '1920' : undefined"
            :height="index === 0 ? '1080' : undefined"
            :style="index === 0 ? { aspectRatio: '16/9' } : {}"
          />

          <div class="content">
            <div class="wrap">
              <h3 class="title">
                {{ item.title }} <i v-if="index === 0">AI in Action</i>
              </h3>
              <p class="desc">{{ item.desc }}</p>
              <div class="realize">
                <a
                  v-if="index !== 0"
                  class="transition"
                  @click="goToSolution(item.url)"
                >
                  了解详情
                </a>
              </div>
            </div>

            <p class="tip">{{ item?.tip }}</p>
          </div>
        </div>
      </a-carousel>
    </div>

    <div v-else-if="bannerList.includes(routeName)" class="other-banner">
      <img :src="content?.banner" alt="图片" fetchpriority="high" />
      <div class="other-content">
        <div>
          <h1 class="other-title">{{ content?.title }}</h1>
          <p class="other-desc">{{ content?.desc }}</p>
          <div class="realize aide-actions" v-if="routeName === 'aide'">
            <a-button
              class="aide-action"
              @click="goToExperience"
              size="large"
            >
              立即体验
            </a-button>
            <a-button
              class="aide-action aide-doc-action"
              size="large"
              @click="router.push('/aide/guide')"
            >
              开发文档
            </a-button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { goToExperience as openExperience } from '@/utils/experience'
import LeftOutlined from '@ant-design/icons-vue/es/icons/LeftOutlined'
import RightOutlined from '@ant-design/icons-vue/es/icons/RightOutlined'

const route = useRoute()
const router = useRouter()

const routeName = route.name

// home
const homeBanner = new URL('/public/image/home/ai-banner.webp', import.meta.url)
const homeBanner1 = new URL(
  '/public/image/home/robot-banner.webp',
  import.meta.url
)
const homeBanner2 = new URL(
  '/public/image/home/openvend-banner.webp',
  import.meta.url
)
const homeBanner3 = new URL(
  '/public/image/home/voice-banner.webp',
  import.meta.url
)
const homeBanner4 = new URL(
  '/public/image/home/retail-banner.webp',
  import.meta.url
)

// other
const coreBanner = new URL('/public/new-image/core-banner.png', import.meta.url)
const aimarketBanner = new URL(
  '/public/image/aimarket-banner.webp',
  import.meta.url
)
const toolBanner = new URL(
  '/public/image/tool/tool-banner.webp',
  import.meta.url
)
const marketBanner = new URL(
  '/public/image/tool/aimarket-banner.webp',
  import.meta.url
)

const bannerList = [
  'home',
  'core',
  'aimarket',
  'Robrain',
  'openVend',
  'voiceAssistant',
  'pectorophony',
  'businessRobot',
  'robotV2',
  'smartRetail',
  'vision_ai',
  'audio_ai',
  'edge_ai',
  'cloud_ai',
  'robotherapist',
  'aiToys',
  'aide',
]

const routeMap = reactive({
  home: [
    {
      title: 'AI开放平台',
      img: homeBanner,
      desc: '硬件智能升级，全链路深度赋能，共筑AIoT共赢生态',
      url: 'algostore',
      tip: '',
    },
    {
      title: 'Robrain AI机器人解决方案',
      img: homeBanner1,
      desc: '端云混合大模型交互方案，让决策更加智能。',
      url: 'solutions/robrain',
      tip: '',
    },
    {
      title: 'OpenVending AI智能识别秤解决方案',
      img: homeBanner2,
      desc: '融合AI视觉算法与深度学习，实现0.1秒级的商品精准自动识别与无感支付，彻底革新传统称重流程。',
      url: 'solutions/smart_scale',
      tip: '',
    },
    {
      title: 'OpenVending AI智能无人零售解决方案',
      img: homeBanner4,
      desc: '以“端侧AI”为核心，为消费者带来“扫码开门，即拿即走”的无缝购物体验。',
      url: 'solutions/smart_vending_machine',
      tip: '',
    },
    {
      title: 'AI音频模组解决方案',
      img: homeBanner3,
      desc: '融合离线语音控制与环境声音分类感知，引领智能家居、家电行业进入主动感知新时代。',
      url: 'solutions/audio_modules',
      tip: '',
    },
  ],
  core: {
    title: '强大的AI技术能力',
    desc: '我们提供端云一体的AI大模型与丰富的算法能力，赋能各行业智能化升级',
    banner: coreBanner,
  },
  vision_ai: {
    title: 'AI视觉算法',
    desc: '以强大的AI视觉算法能力，打造场景化智能新生态',
    banner: coreBanner,
  },
  audio_ai: {
    title: 'AI音频算法',
    desc: '以全链路AI音频算法能力，赋能听觉智能新体验',
    banner: coreBanner,
  },
  edge_ai: {
    title: '端侧AI大模型',
    desc: '实时响应、离线智能、成本可控、隐私安全',
    banner: coreBanner,
  },
  cloud_ai: {
    title: '端云融合AI Agent',
    desc: '“端侧智能+云端大模型”协同，赋能千行百业智能变革',
    banner: coreBanner,
  },
  aimarket: {
    title: 'AlgoStore算法超市',
    desc: '一站式算法服务平台，提供多元算法资源与灵活调用方式，助力快速构建智能化能力',
    banner: marketBanner,
  },
  Robrain: {
    title: 'Robrain AI机器人解决方案',
    desc: '端云混合大模型语音交互方案，让体验更好，决策更加智能',
    banner: aimarketBanner,
  },
  openVend: {
    title: 'OpenVending AI智能识别秤解决方案',
    desc: '',
    banner: aimarketBanner,
  },
  pectorophony: {
    title: 'AI音频模组解决方案',
    desc: '融合离线语音控制与环境声音分类感知，引领智能家居、家电行业进入主动感知新时代',
    banner: aimarketBanner,
  },
  businessRobot: {
    title: 'AI商业服务机器人解决方案',
    desc: '',
    banner: aimarketBanner,
  },
  robotherapist: {
    title: 'AI具身理疗机器人解决方案',
    desc: '',
    banner: aimarketBanner,
  },
  smartRetail: {
    title: 'OpenVending AI智能无人零售解决方案',
    desc: '',
    banner: aimarketBanner,
  },
  aiToys: {
    title: 'AI玩具解决方案',
    desc: '',
    banner: aimarketBanner,
  },
  aide: {
    title: 'AIDE开发工具链',
    desc: '让端侧AI落地快人一步',
    banner: toolBanner,
  },
})

let content = computed(() => routeMap[routeName])

const goToSolution = (route) => {
  router.push(`/${route}`)
}

const goToExperience = () => {
  openExperience(router, '/aide/model_convert')
}
</script>

<style lang="scss" scoped>
* {
  box-sizing: border-box;
}
.banner {
  .transition {
    // 只使用 transform 和 opacity，避免触发重排，确保合成动画
    transition: transform 0.3s cubic-bezier(0.15, 0.83, 0.66, 1),
      opacity 0.3s cubic-bezier(0.15, 0.83, 0.66, 1);
    -webkit-transition: transform 0.3s cubic-bezier(0.15, 0.83, 0.66, 1),
      opacity 0.3s cubic-bezier(0.15, 0.83, 0.66, 1);
    // 提示浏览器优化
    will-change: transform, opacity;
  }

  @media (min-width: 576px) {
    .carousel-item {
      height: 335.78px !important;
    }
    .content {
      max-width: 540px !important;
      i {
        font-size: 17px !important;
      }
    }

    :deep() {
      .ant-carousel {
        .slick-dots {
          margin-bottom: 1.5rem !important;
        }
      }
    }
  }
  @media (min-width: 768px) {
    .carousel-item {
      height: 317.19px !important;
    }
    .content {
      max-width: 720px !important;
      .title {
        font-size: 36px !important;
      }
      i {
        font-size: 18px !important;
      }
      .desc {
        font-size: 24px !important;
      }
    }

    :deep() {
      .ant-carousel {
        .slick-dots {
          max-width: 720px !important;
        }
      }
    }

    .other-content {
      h1 {
        font-size: 36px !important;
      }

      p {
        font-size: 18px !important;
      }
    }
  }
  @media screen and (max-width: 768px) {
    :deep(.slick-arrow.custom-slick-arrow) {
      display: none !important;
    }
  }
  @media screen and (max-width: 992px) {
    .content {
      padding-bottom: 50px !important;
    }
  }
  @media (min-width: 992px) {
    .carousel-item {
      height: 394.19px !important;
    }
    .content {
      max-width: 960px !important;
      .title {
        font-size: 46px !important;
      }
      i {
        font-size: 20px !important;
      }
      .desc {
        font-size: 28px !important;
      }
      // .tip {
      //   padding-top: 3rem !important;
      // }
    }

    :deep() {
      .ant-carousel {
        .slick-dots {
          max-width: 960px !important;
          margin-bottom: 3rem !important;
          // bottom: 3rem !important;
          height: 24px !important;

          li {
            width: 40px !important;
          }
        }
      }
    }

    .other-content {
      h1 {
        font-size: 40px !important;
      }

      p {
        font-size: 22px !important;
      }
    }
  }
  @media screen and (max-width: 1200px) {
    .content {
      min-height: min(31.25vw, 600px) !important;
    }
  }
  @media (min-width: 1200px) {
    .carousel-item {
      height: 600px !important;
    }
    .content {
      max-width: 1140px !important;
      .title {
        font-size: 50px !important;
      }
      i {
        font-size: 24px !important;
      }
      .desc {
        font-size: 32px !important;
        padding-top: 0.25rem;
      }
      .realize {
        a {
          font-size: 18px !important;
        }
      }
    }

    :deep() {
      .ant-carousel {
        .slick-dots {
          max-width: 1140px !important;
          // bottom: 3rem !important;
        }
      }
    }

    .other-content {
      h1 {
        font-size: 44px !important;
      }

      p {
        font-size: 24px !important;
      }
    }
  }
  @media (min-width: 1400px) {
    .content {
      max-width: 1320px !important;
      .title {
        font-size: 56px !important;
      }
    }
    :deep() {
      .ant-carousel {
        .slick-dots {
          max-width: 1320px !important;
        }
      }
    }
  }
  @media (min-width: 1500px) {
    .content {
      max-width: 1448px !important;
    }
    :deep() {
      .ant-carousel {
        .slick-dots {
          max-width: 1448px !important;
        }
      }
    }
  }

  .home-banner {
    position: relative;
    overflow: hidden;
    width: 100%;
    // 设置初始高度，避免布局偏移（CLS）
    min-height: 335.78px; // 移动端最小高度

    .carousel-item {
      width: 100%;
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      position: relative;
      // 设置最小高度，避免布局偏移（CLS 优化）
      min-height: 335.78px; // 移动端最小高度，与媒体查询保持一致

      img {
        position: absolute;
        left: 0;
        top: 0;
        width: 100%;
        height: 100%;
        object-fit: cover;
        // 使用 will-change 提示浏览器优化
        will-change: transform;
      }

      .content {
        position: relative;
        width: 100%;
        padding-right: calc(3rem * 0.5);
        padding-left: calc(3rem * 0.5);
        margin-right: auto;
        margin-left: auto;
        display: flex;
        flex-wrap: wrap;
        padding-top: 3rem;
        min-height: 600px;
        padding-bottom: 100px;

        .wrap {
          max-width: 980px;
          width: 100%;
          margin-top: auto !important;
          color: #fff;
        }

        .title {
          font-size: 32px;
          white-space: pre-wrap;
          font-weight: 400;
          line-height: 1.2;
          margin-bottom: 0;

          i {
            display: inline-block;
            background: linear-gradient(90deg, #f84c9a 0, #2c48ff 100%);
            border-radius: 28px 28px 28px 0;
            padding: 10px 24px;
            font-size: 17px;
            vertical-align: bottom;
            margin-bottom: 4px;
            line-height: 1.5;
            font-style: normal;
          }
        }

        .desc {
          line-height: 1.25 !important;
          font-size: 20px;
          margin-top: 1rem;
          margin-bottom: 0;
          font-family: 'Stratos-Regular';
        }

        .tip {
          font-size: 14px;
          color: #fff;
          opacity: 0.5;
          // padding-top: 1.5rem;
          margin-top: auto !important;
          margin-bottom: 0;
          width: 100%;
          max-width: 900px;
        }
      }
    }
  }

  .realize {
    padding-top: 0.25rem;
    margin-top: 1rem;
    font-family: Stratos-Regular;

    a {
      display: inline-block;
      background-color: transparent;
      color: #fff;
      font-size: 16px;
      border: 1px solid #fff;
      padding: 12px 18px;
      border-radius: 0.25rem;
      text-decoration: none;
      line-height: 1.5;

      &:hover {
        border-color: #ed000c;
      }
    }
  }

  .other-banner {
    height: 368px;
    width: 100%;
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;

    img {
      width: 100%;
      height: 100%;
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      object-fit: cover;
    }

    .other-content {
      position: absolute;
      color: #fff;
      height: 100%;
      width: 100%;
      display: flex;
      align-items: center;
      justify-content: center;

      h1 {
        text-align: center;
        font-size: 30px;
        font-weight: 600;
        height: 62px;
        margin-bottom: 1rem;
      }

      p {
        font-size: 16px;
        text-align: center;
        font-weight: 400;
      }
    }
  }

  .aide-actions {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 14px;

    .aide-action {
      width: 160px;
      height: 50px;
      font-size: 19px;
    }

    .aide-doc-action {
      color: #fff;
      background: rgba(255, 255, 255, 0.13);
      border-color: rgba(255, 255, 255, 0.8);
      backdrop-filter: blur(8px);

      &:hover {
        color: #fff;
        background: rgba(255, 255, 255, 0.24);
        border-color: #fff;
      }
    }
  }
}

:deep(.slick-arrow.custom-slick-arrow) {
  text-align: center;
  width: calc(76px / 44 * 27);
  height: 76px;
  line-height: 76px;
  font-size: 25px;
  color: #fff;
  // 只使用 transform 和 opacity，确保合成动画（避免非合成动画）
  transition: transform 0.3s ease, opacity 0.3s ease;
  opacity: 0.3;
  z-index: 10;
  transform: translateY(-50%);
  margin-top: unset;
  will-change: transform, opacity;
}
:deep(.slick-arrow.custom-slick-arrow:before) {
  display: none;
}
:deep(.slick-arrow.custom-slick-arrow:hover) {
  color: #fff;
  // 使用 transform 而不是 background-color，确保合成动画
  background-color: rgba(0, 0, 0, 0.3);
  opacity: 1;
  // 如果需要缩放效果，使用 transform: scale() 而不是改变尺寸
}
:deep(.slick-arrow.custom-slick-arrow.slick-prev) {
  left: 0px;
  border-bottom-left-radius: 0.375rem !important;
  border-top-left-radius: 0.375rem !important;
}
:deep(.slick-arrow.custom-slick-arrow.slick-next) {
  right: 0px;
  border-bottom-left-radius: 0.375rem !important;
  border-top-left-radius: 0.375rem !important;
}

:deep() {
  .ant-carousel {
    .slick-dots {
      box-sizing: border-box;
      justify-content: flex-start;
      width: 100%;
      max-width: 900px;
      padding-right: calc(3rem * 0.5);
      padding-left: calc(3rem * 0.5);
      margin-right: auto;
      margin-left: auto;
      margin-bottom: 1rem;
      bottom: 0;
      overflow: hidden;

      li {
        width: 20px;
        display: flex;
        align-items: center;
        height: 21px;
        transform: translate3d(0px, 3px, 0);

        &:first-child {
          margin-inline-start: 0;
        }

        button {
          height: 4px;
        }
      }
    }
  }
}
</style>
